import { RefObject, useEffect } from "react";

/**
 * The designed pages' entrance motion: each block in a section fades up as
 * it scrolls into view, and children of a `data-stagger` container follow
 * 80ms apart. Mirrors the script the pages were authored with. Skipped for
 * reduced-motion users and where IntersectionObserver is missing, so the
 * page is always fully visible without it.
 */
export function useRevealOnScroll(root: RefObject<HTMLElement>): void {
  useEffect(() => {
    const container = root.current;
    if (
      !container ||
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const targets: [HTMLElement, number][] = [];
    container
      .querySelectorAll<HTMLElement>("main section > div > *")
      .forEach((el) => {
        if (el.hasAttribute("data-stagger")) {
          Array.from(el.children).forEach((child, i) =>
            targets.push([child as HTMLElement, i * 80]),
          );
        } else {
          targets.push([el, 0]);
        }
      });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          el.style.opacity = "1";
          el.style.transform = "none";
          observer.unobserve(el);
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" },
    );

    targets.forEach(([el, delay]) => {
      el.style.opacity = "0";
      el.style.transform = "translateY(24px)";
      el.style.transition = `opacity 700ms cubic-bezier(.16,1,.3,1) ${delay}ms, transform 700ms cubic-bezier(.16,1,.3,1) ${delay}ms`;
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, [root]);
}
